import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

const Home = lazy(() => import("@/pages/Home"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const BlogCategoryPage = lazy(() => import("@/pages/BlogCategoryPage"));
const PlatformHubPage = lazy(() => import("@/pages/PlatformHubPage"));
const AdultworkStatusPage = lazy(() => import("@/pages/AdultworkStatusPage"));
const AdminStatusReportsPage = lazy(() => import("@/pages/AdminStatusReportsPage"));
const StatusHubPage = lazy(() => import("@/pages/StatusHubPage"));
const SitesHubPage = lazy(() => import("@/pages/SitesHubPage"));
const HelpCentreHome = lazy(() => import("@/pages/HelpCentreHome"));
const HelpCategoryPage = lazy(() => import("@/pages/HelpCategoryPage"));
const HelpPlatformPage = lazy(() => import("@/pages/HelpPlatformPage"));
const HelpArticlePage = lazy(() => import("@/pages/HelpArticlePage"));
const HelpSearchPage = lazy(() => import("@/pages/HelpSearchPage"));
const RemovedPage = lazy(() => import("@/pages/RemovedPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const getCanonicalSiteUrl = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return siteUrl.replace(/\/+$/, "");
};

const CANONICAL_PATH_REDIRECTS = {
  "/index.html": "/",
  "/home": "/",
  "/adultwork": "/sites/adultwork",
  "/stripchat": "/sites/stripchat",
  "/cam4": "/sites/cam4",
  "/chaturbate": "/sites/chaturbate",
  "/loyalfans": "/sites/loyalfans",
  "/myclub": "/sites/myclub",
  "/iwantclips": "/sites/iwantclips",
  "/skyprivate": "/sites/skyprivate",
  "/adultwork-status": "/status/adultwork",
  "/stripchat-status": "/status/stripchat",
  "/chaturbate-status": "/status/chaturbate",
  "/loyalfans-status": "/status/loyalfans",
  "/iwantclips-status": "/status/iwantclips",
  "/myclub-status": "/status/myclub",
  "/skyprivate-status": "/status/skyprivate",
  "/cam4-status": "/status/cam4",
};

const normalizeCanonicalPath = (pathname = "/") => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  const cleanPath = pathname.replace(/\/+$/, "") || "/";
  return CANONICAL_PATH_REDIRECTS[cleanPath.toLowerCase()] || cleanPath;
};

function SeoRouteManager() {
  const location = useLocation();

  useEffect(() => {
    const cleanPath = (location.pathname || "/").replace(/\/+$/, "") || "/";
    const normalizedPath = normalizeCanonicalPath(location.pathname);
    const canonicalSiteUrl = getCanonicalSiteUrl();
    const canonicalHref = `${canonicalSiteUrl}${normalizedPath === "/" ? "/" : normalizedPath}`;
    const isAliasPath = cleanPath.toLowerCase() !== normalizedPath.toLowerCase();

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalHref);

    let ogUrlMeta = document.querySelector('meta[property="og:url"]');
    if (!ogUrlMeta) {
      ogUrlMeta = document.createElement("meta");
      ogUrlMeta.setAttribute("property", "og:url");
      document.head.appendChild(ogUrlMeta);
    }
    ogUrlMeta.setAttribute("content", canonicalHref);

    let routeManagedRobotsMeta = document.querySelector('meta[name="robots"][data-route-managed="true"]');

    if (isAliasPath) {
      if (!routeManagedRobotsMeta) {
        routeManagedRobotsMeta = document.createElement("meta");
        routeManagedRobotsMeta.setAttribute("name", "robots");
        routeManagedRobotsMeta.setAttribute("data-route-managed", "true");
        document.head.appendChild(routeManagedRobotsMeta);
      }
      routeManagedRobotsMeta.setAttribute("content", "noindex,follow");
    } else if (routeManagedRobotsMeta) {
      routeManagedRobotsMeta.remove();
    }
  }, [location.pathname]);

  return null;
}

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b1020] px-4 text-white" data-testid="route-loading-fallback">
      <div className="rounded-3xl border border-white/12 bg-[#151c30] px-6 py-5 text-center shadow-[0_20px_50px_rgba(8,12,24,0.35)]">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-[#ff7bb8]" />
        <p className="mt-4 text-sm text-white/72">Loading…</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <>
      <SeoRouteManager />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/prize/*" element={<RemovedPage title="Page removed" description="This page has been permanently removed." />} />
          <Route path="/shop/*" element={<RemovedPage title="Page removed" description="This page has been permanently removed." />} />
          <Route path="/" element={<Home />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="/Home" element={<Navigate to="/" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/help-centre" element={<HelpCentreHome />} />
          <Route path="/help-centre/search" element={<HelpSearchPage />} />
          <Route path="/help-centre/platforms/:platformSlug/:articleSlug" element={<HelpArticlePage />} />
          <Route path="/help-centre/platforms/:platformSlug" element={<HelpPlatformPage />} />
          <Route path="/help-centre/:categorySlug/:articleSlug" element={<HelpArticlePage />} />
          <Route path="/help-centre/:categorySlug" element={<HelpCategoryPage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/payments" element={<BlogCategoryPage />} />
          <Route path="/blog/stripchat-payment-delays" element={<BlogPost />} />
          <Route path="/sites" element={<SitesHubPage />} />
          <Route path="/sites/adultwork" element={<PlatformHubPage platformKey="adultwork" />} />
          <Route path="/sites/stripchat" element={<PlatformHubPage platformKey="stripchat" />} />
          <Route path="/sites/cam4" element={<PlatformHubPage platformKey="cam4" />} />
          <Route path="/sites/chaturbate" element={<PlatformHubPage platformKey="chaturbate" />} />
          <Route path="/sites/loyalfans" element={<PlatformHubPage platformKey="loyalfans" />} />
          <Route path="/sites/myclub" element={<PlatformHubPage platformKey="myclub" />} />
          <Route path="/sites/iwantclips" element={<PlatformHubPage platformKey="iwantclips" />} />
          <Route path="/sites/skyprivate" element={<PlatformHubPage platformKey="skyprivate" />} />
          <Route path="/adultwork" element={<Navigate to="/sites/adultwork" replace />} />
          <Route path="/stripchat" element={<Navigate to="/sites/stripchat" replace />} />
          <Route path="/cam4" element={<Navigate to="/sites/cam4" replace />} />
          <Route path="/chaturbate" element={<Navigate to="/sites/chaturbate" replace />} />
          <Route path="/loyalfans" element={<Navigate to="/sites/loyalfans" replace />} />
          <Route path="/myclub" element={<Navigate to="/sites/myclub" replace />} />
          <Route path="/iwantclips" element={<Navigate to="/sites/iwantclips" replace />} />
          <Route path="/skyprivate" element={<Navigate to="/sites/skyprivate" replace />} />
          <Route path="/status" element={<StatusHubPage />} />
          <Route path="/status/adultwork" element={<AdultworkStatusPage platformKey="adultwork" />} />
          <Route path="/status/stripchat" element={<AdultworkStatusPage platformKey="stripchat" />} />
          <Route path="/status/chaturbate" element={<AdultworkStatusPage platformKey="chaturbate" />} />
          <Route path="/status/loyalfans" element={<AdultworkStatusPage platformKey="loyalfans" />} />
          <Route path="/status/iwantclips" element={<AdultworkStatusPage platformKey="iwantclips" />} />
          <Route path="/status/myclub" element={<AdultworkStatusPage platformKey="myclub" />} />
          <Route path="/status/skyprivate" element={<AdultworkStatusPage platformKey="skyprivate" />} />
          <Route path="/status/cam4" element={<AdultworkStatusPage platformKey="cam4" />} />
          <Route path="/adultwork-status" element={<Navigate to="/status/adultwork" replace />} />
          <Route path="/stripchat-status" element={<Navigate to="/status/stripchat" replace />} />
          <Route path="/chaturbate-status" element={<Navigate to="/status/chaturbate" replace />} />
          <Route path="/loyalfans-status" element={<Navigate to="/status/loyalfans" replace />} />
          <Route path="/iwantclips-status" element={<Navigate to="/status/iwantclips" replace />} />
          <Route path="/myclub-status" element={<Navigate to="/status/myclub" replace />} />
          <Route path="/skyprivate-status" element={<Navigate to="/status/skyprivate" replace />} />
          <Route path="/cam4-status" element={<Navigate to="/status/cam4" replace />} />
          <Route path="/fmp-admin/status-reports" element={<AdminStatusReportsPage />} />
          <Route path="/terms" element={<PolicyPage policyKey="terms" />} />
          <Route path="/privacy" element={<PolicyPage policyKey="privacy" />} />
          <Route path="/2257" element={<PolicyPage policyKey="usc2257" />} />
          <Route path="/anti-trafficking" element={<PolicyPage policyKey="antiTrafficking" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  useEffect(() => {
    const readyId = window.requestAnimationFrame(() => {
      document.body.setAttribute("data-app-ready", "true");
    });

    return () => {
      window.cancelAnimationFrame(readyId);
      document.body.removeAttribute("data-app-ready");
    };
  }, []);

  useEffect(() => {
    const combinedSelector = [
      "#emergent-badge",
      ".emergent-badge",
      "a[href*='emergent']",
      "a[title*='Emergent']",
      "iframe[src*='emergent']",
      "iframe[title*='Emergent']",
      "[data-emergent-badge]",
    ].join(", ");

    let frameId = null;
    let stopTimer = null;

    const removeEmergentBadge = () => {
      document.querySelectorAll(combinedSelector).forEach((node) => node.remove());

      document.querySelectorAll("a, div, span, p").forEach((node) => {
        if (node.textContent && node.textContent.includes("Made with Emergent")) {
          node.remove();
        }
      });
    };

    const scheduleCleanup = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        removeEmergentBadge();
      });
    };

    removeEmergentBadge();

    const observer = new MutationObserver(scheduleCleanup);
    observer.observe(document.body, { childList: true, subtree: true });

    stopTimer = window.setTimeout(() => {
      observer.disconnect();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }
    }, 4000);

    return () => {
      if (stopTimer !== null) {
        window.clearTimeout(stopTimer);
      }
      observer.disconnect();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
